import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SituViewWebApiClient, AssetValidationRule, GetAssetValidationRequest, FileVaultReferenceRelation, RuleResult } from 'situview-webapi-client';
import { AuthService, StateService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

@Component({
  selector: 'asset-validation',
  templateUrl: './asset-validation.component.html',
  styleUrls: ['./asset-validation.component.less']
})
export class AssetValidationComponent extends SituBaseComponent {
  
  @Input() companyId?: string;
  @Input() fileVaultReferenceRelationId?: string;
  @Input() fileVaultHash?: string;
  @Input() callerReferenceKey?: string;
  @Input() autoLoadAssetValidation = false;
  @Output() validationLoaded: EventEmitter<{validationRules: { key: string; value: AssetValidationRule; }[], worstResult: RuleResult}> = new EventEmitter();

  assetValidation?: { key: string; value: AssetValidationRule; }[];
  
  showAssetValidationDetails = false;

  ruleResult = RuleResult;
  worstResult: RuleResult = RuleResult.None;

  static validationCache: Record<string, {
      key: string;
      value: AssetValidationRule;
    }[]> = {};

  constructor(
    authService: AuthService,
    stateService: StateService,
    private situViewClient: SituViewWebApiClient,
  ) {
    super(authService, stateService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadAssetValidation();
  }

  async loadAssetValidation(force: boolean = false) {
    if (this.fileVaultReferenceRelationId) {
      var cacheKey = `${this.fileVaultReferenceRelationId}|${this.fileVaultHash}|${this.companyId}`;
    } else if (this.callerReferenceKey) {
      var cacheKey = `${this.callerReferenceKey}`;
    } else {
      return;
    }

    this.assetValidation = AssetValidationComponent.validationCache[cacheKey];

    if (!this.autoLoadAssetValidation && !force) {
      //console.log(`Skipped ${cacheKey}`);
      return;
    }

    if (this.isLoaded()) {
      // Already loading or loaded, Skip it
      //console.log(`Cached ${cacheKey}`);
      this.worstResult = AssetValidationComponent.getWorstAssetValidationIssueLevel(this.assetValidation);
      this.validationLoaded.emit({validationRules: this.assetValidation, worstResult: this.worstResult});
      return;
    }

    if (this.fileVaultReferenceRelationId || this.callerReferenceKey) {
        this.assetValidation ??= [];
        AssetValidationComponent.validationCache[cacheKey] = this.assetValidation;

        var request = new GetAssetValidationRequest();
        request.companyId = this.companyId;

        if (this.fileVaultReferenceRelationId) {
          request.fileVaultReferenceId = this.fileVaultReferenceRelationId;
        } else {
          request.callerReferenceKey = this.callerReferenceKey;
        }
        
        try {
          var requestResponse = await this.situViewClient.getAssetValidation(request);
  
          if (!requestResponse.success) {
            return;
          }
          
          this.assetValidation.length = 0;
          for (let [key, value] of Object.entries(requestResponse.assetValidation!)) {
            this.assetValidation.push({ key, value });
          }
        } catch (e) {
          console.error(e);

          var loadFailedAssetValidationRule = new AssetValidationRule();
          loadFailedAssetValidationRule.result = RuleResult.Failure;
          loadFailedAssetValidationRule.reason = "Failed to get validation results"
          loadFailedAssetValidationRule.value = false;
          this.assetValidation.push({key: "Validation Completed", value: loadFailedAssetValidationRule})
        }

        //console.log(`Completed ${cacheKey}`);
    }

    this.worstResult = AssetValidationComponent.getWorstAssetValidationIssueLevel(this.assetValidation);
    this.validationLoaded.emit({validationRules: this.assetValidation, worstResult: this.worstResult});
  }

  isLoading() {
    return this.assetValidation && this.assetValidation.length === 0;
  }

  isLoaded() {
    return this.assetValidation && this.assetValidation.length > 0;
  }
  
  public static getWorstAssetValidationIssueLevel(assetValidation?: { key: string; value: AssetValidationRule; }[]) {
    if (!assetValidation) {
      return RuleResult.None;
    }

    var worstResult = assetValidation
      .map(x => x.value.result ?? RuleResult.None)
      .reduce((max, current) => Math.max(max, current), RuleResult.None);

    return worstResult;
  }
}